import React, { useContext, useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Tabs,
  Tab,
  Badge,
  Typography,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Chat as ChatIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { ChatContext } from '../../contexts/ChatContext';
import UserList from './UserList';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import ChatSettings from './ChatSettings';

const ChatSidebar = () => {
  const {
    isChatOpen,
    setIsChatOpen,
    activeConversation,
    setActiveConversation,
    unreadCounts,
    chatWidth,
    setChatWidth
  } = useContext(ChatContext);
  const [activeTab, setActiveTab] = useState(0); // 0 = Chats, 1 = Users
  const [showSettings, setShowSettings] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // Handle resize start
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle resize (mouse move)
  useEffect(() => {
    const handleResize = (e) => {
      if (!isResizing) return;

      // Calculate new width (from right edge of screen)
      const newWidth = window.innerWidth - e.clientX;

      // Constrain zwischen min (300px) und max (800px oder 60% of screen)
      const minWidth = 300;
      const maxWidth = Math.min(800, window.innerWidth * 0.6);
      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setChatWidth(constrainedWidth);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, setChatWidth]);

  // Reset width when closing chat
  const handleClose = () => {
    setActiveConversation(null);
    setShowSettings(false);
    setIsChatOpen(false);
    setChatWidth(400); // Reset to default
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '64px',
        right: 0,
        height: 'calc(100% - 64px)',
        width: `${chatWidth}px`,
        transform: isChatOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: isResizing ? 'none' : 'transform 0.3s ease', // Disable transition beim Resizen
        bgcolor: 'background.paper',
        boxShadow: 3,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Resize Handle - vertikaler Balken am linken Rand */}
      <Box
        ref={resizeRef}
        onMouseDown={handleResizeStart}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '8px',
          cursor: 'ew-resize',
          bgcolor: isResizing ? 'primary.main' : 'transparent',
          transition: 'background-color 0.2s',
          zIndex: 1300,
          '&:hover': {
            bgcolor: 'primary.light',
            opacity: 0.5
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <DragIndicatorIcon
          sx={{
            fontSize: 16,
            color: isResizing ? 'white' : 'action.disabled',
            transform: 'rotate(90deg)',
            opacity: isResizing ? 1 : 0,
            transition: 'opacity 0.2s',
            '$parent:hover &': {
              opacity: 0.7
            }
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showSettings ? (
              <>
                <IconButton
                  onClick={() => setShowSettings(false)}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                  Einstellungen
                </Typography>
              </>
            ) : (
              <>
                <Badge badgeContent={totalUnread} color="error">
                  <ChatIcon color="primary" />
                </Badge>
                <Typography variant="h6">
                  Chat
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!showSettings && !activeConversation && (
              <>
                <IconButton
                  onClick={() => setShowSettings(true)}
                  size="small"
                >
                  <SettingsIcon />
                </IconButton>
              </>
            )}
            <IconButton
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Tabs (nur anzeigen wenn keine aktive Conversation und nicht in Settings) */}
        {!activeConversation && !showSettings && (
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<ChatIcon />}
              label="Chats"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<PeopleIcon />}
              label="Users"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        )}

        {/* Content */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showSettings ? (
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              <ChatSettings />
            </Box>
          ) : activeConversation ? (
            <ChatWindow />
          ) : (
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {activeTab === 0 ? (
                <ConversationList />
              ) : (
                <UserList />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatSidebar;
